import { Tooltip } from "antd";

export type Bar = { key: string; label: string; value: number; tip: string };

/**
 * A daily trend as bars: one series in the brand color, rounded data ends on
 * a recessive baseline, 2px gaps, a hover tooltip per day (the whole column is
 * the hit target), the peak and a few dates labelled. Screens keep a table
 * view beside it for exact numbers.
 */
export function BarChart({ bars, height = 180, ariaLabel, format }: { bars: Bar[]; height?: number; ariaLabel: string; format: (n: number) => string }) {
  const w = 720;
  const top = 18;
  const bottom = 22;
  const plot = height - top - bottom;
  const max = Math.max(1, ...bars.map((b) => b.value));
  const step = w / Math.max(1, bars.length);
  const bw = Math.max(2, step - 2);
  // A date under the first, last and a few bars between, never crowded.
  const every = Math.max(1, Math.ceil(bars.length / 6));
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" role="img" aria-label={ariaLabel} style={{ display: "block" }}>
      <text x={0} y={11} fontSize={11} fill="var(--z-muted)">
        {format(max)}
      </text>
      <line x1={0} x2={w} y1={top + plot + 0.5} y2={top + plot + 0.5} stroke="var(--z-line)" />
      {bars.map((b, i) => {
        const h = b.value > 0 ? Math.max(2, (b.value / max) * plot) : 0;
        const x = i * step + 1;
        const y = top + plot - h;
        const r = Math.min(4, bw / 2, h);
        return (
          <Tooltip key={b.key} title={b.tip} mouseEnterDelay={0}>
            <g style={{ cursor: "default" }}>
              <rect x={i * step} y={top} width={step} height={plot} fill="transparent" />
              {h > 0 && (
                <path
                  d={`M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + bw - r} Q${x + bw},${y} ${x + bw},${y + r} V${y + h} Z`}
                  fill="var(--z-brand)"
                />
              )}
              {(i % every === 0 || i === bars.length - 1) && (
                <text x={x + bw / 2} y={height - 6} fontSize={11} textAnchor="middle" fill="var(--z-muted)">
                  {b.label}
                </text>
              )}
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
}
